import { z, type ZodType } from 'zod';

const paginationSchema = z.object({ hasMore: z.boolean(), nextCursor: z.string().nullish() });

/** Collect a cursor list without returning partial results after failure or cancellation. */
export async function collectCursorList<T extends { id: string }>(
  fetchPage: (cursor?: string) => Promise<unknown>,
  itemSchema: ZodType<T>,
  label: string,
  signal?: AbortSignal
): Promise<T[]> {
  const items = new Map<string, T>();
  const seenCursors = new Set<string>();
  let cursor: string | undefined;
  for (;;) {
    signal?.throwIfAborted();
    const response = await fetchPage(cursor);
    signal?.throwIfAborted();
    // A Go nil slice encodes as `data: null`: a verified empty page, not drift.
    const page = z.object({ data: z.array(itemSchema).nullable() }).parse(response).data ?? [];
    const pagination = paginationSchema.parse(response);
    for (const item of page) items.set(item.id, item);
    if (!pagination.hasMore) return [...items.values()];
    if (!pagination.nextCursor || seenCursors.has(pagination.nextCursor)) {
      throw new Error(`${label}: pagination did not advance`);
    }
    cursor = pagination.nextCursor;
    seenCursors.add(cursor);
  }
}
